import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Broom, Hourglass, Users, Clock, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import '../pages/ProviderDashboard.css';

const Services = () => {
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'customer', direction: 'asc' });
  const [searchParams] = useSearchParams();
  const unallocatedOnly = searchParams.get('filter') === 'unallocated';

  useEffect(() => {
    if (authLoading) return;

    let isMounted = true;
    const controller = new AbortController();

    const toDisplayDate = (isoDate) => {
      if (!isoDate) return '';
      const parsed = new Date(isoDate);
      if (Number.isNaN(parsed.getTime())) return String(isoDate);

      const day = parsed.getDate();
      const month = parsed.toLocaleString('en-AU', { month: 'short' });
      const year = String(parsed.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    };

    const loadServices = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/services?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
          signal: controller.signal,
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': (activeUserRoles || []).join(','),
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to load services data: ${res.status}`);
        }

        const servicesData = await res.json();
        const normalizedServices = (Array.isArray(servicesData) ? servicesData : []).map((service) => {
          return {
            id: String(service.id),
            customerId: String(service.customerId || service.raw?.client?.id || service.raw?.client_id || ''),
            customer: service.customer || service.displayName || 'Unknown customer',
            risk: service.risk || service.riskProfile || 'Low',
            serviceType: service.serviceType || service.name || 'Care Service',
            duration: service.duration || '45 mins',
            startDate: toDisplayDate(service.startDate),
            worker: service.worker || null,
            defaultWorker: service.defaultWorker || null,
            defaultWorkerId: service.defaultWorkerId || null,
            status: service.status || 'Active',
            visitsCompleted: service.visitsCompleted ?? 0,
            raw: service.raw || service,
          };
        });

        if (isMounted) {
          setServices(normalizedServices);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading services:', err);
          if (isMounted) {
            setError(err.message);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadServices();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [authLoading, activeEmployeeId, activeUserEmail, activeUserRoles]);

  const renderWorkerAllocation = (service) => (
    <span>{service.defaultWorker || 'Unassigned'}</span>
  );

  const getSortValue = (service, key) => {
    switch (key) {
      case 'customer':
        return (service.customer || '').toLowerCase();
      case 'serviceType':
        return (service.serviceType || '').toLowerCase();
      case 'duration':
        return Number((service.duration || '').replace(/[^0-9]/g, '')) || 0;
      case 'worker':
        return (service.defaultWorker || '').toLowerCase();
      case 'status':
        return service.status === 'Active' ? 1 : 0;
      default:
        return '';
    }
  };

  const visibleServices = unallocatedOnly
    ? services.filter((service) => service.status.toLowerCase() === 'active' && !service.defaultWorkerId)
    : services;
  const sortedServices = [...visibleServices].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) return <div className="loading">Loading services...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="provider-dashboard">
      <div className="page-header">
        <h1>Services</h1>
      </div>
      {unallocatedOnly && (
        <div className="active-filters">
          <span className="filter-tag">Unallocated services</span>
          <Link to="/provider/services">Show all services</Link>
        </div>
      )}

      <div className="table-section">
        {/* Mobile Card View */}
        <div className="mobile-cards">
          {sortedServices && sortedServices.length > 0 ? (
            sortedServices.map((service) => {
              return (
                <div key={service.id} className="service-card">
                  <div className="card-header">
                    <h3 className="card-customer">{service.customer}</h3>
                    <span className={`status-badge ${service.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                      {service.status}
                    </span>
                  </div>
                  <div className="card-details-compact">
                    <div className="card-info-item">
                      <Broom size={16} weight="regular" className="card-icon" />
                      <span>{service.serviceType}</span>
                    </div>
                    <div className="card-info-item">
                      <Hourglass size={16} weight="regular" className="card-icon" />
                      <span>{service.duration}</span>
                    </div>
                    <div className="card-info-item">
                      <Users size={16} weight="regular" className="card-icon" />
                      {renderWorkerAllocation(service)}
                    </div>
                  </div>
                  <div className="card-actions">
                    <Link to={`/provider/services/${service.id}/edit-service`} className="action-button btn-icon" title="Edit Service">
                      <PencilSimple size={24} weight="regular" />
                    </Link>
                    <Link to={`/provider/schedule?service=${encodeURIComponent(service.id)}&customer=${encodeURIComponent(service.customer)}&clientId=${encodeURIComponent(service.customerId)}&limit=30`} className="action-button btn-icon" title="View Schedule">
                      <Clock size={24} weight="regular" />
                    </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="empty-state">No services found.</p>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('customer')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Customer {getSortIndicator('customer')}</button></th>
                <th><button type="button" onClick={() => handleSort('serviceType')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Service Type {getSortIndicator('serviceType')}</button></th>
                <th><button type="button" onClick={() => handleSort('duration')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Duration {getSortIndicator('duration')}</button></th>
                <th><button type="button" onClick={() => handleSort('worker')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Default Worker {getSortIndicator('worker')}</button></th>
                <th><button type="button" onClick={() => handleSort('status')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}>Status {getSortIndicator('status')}</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedServices && sortedServices.length > 0 ? (
                sortedServices.map((service) => {
                  return (
                    <tr key={service.id}>
                      <td>{service.customer}</td>
                      <td>{service.serviceType}</td>
                      <td>{service.duration}</td>
                      <td>
                        {renderWorkerAllocation(service)}
                      </td>
                      <td>
                        <span className={`status-badge ${service.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                          {service.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link to={`/provider/services/${service.id}/edit-service`} className="action-button btn-icon" title="Edit Service">
                            <PencilSimple size={24} weight="regular" />
                          </Link>
                          <Link to={`/provider/schedule?service=${encodeURIComponent(service.id)}&customer=${encodeURIComponent(service.customer)}&clientId=${encodeURIComponent(service.customerId)}&limit=30`} className="action-button btn-icon" title="View Schedule">
                            <Clock size={24} weight="regular" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No services found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Services;
